#include <uv_pars_vertex>
varying float vMaskAlpha;
varying float vHoverMix;
varying float vAirplaneMix;
varying float vAirplaneProximity;
varying float vDustWakeMix;
varying float vRadarMix;
varying float vRadarHeight;
varying float vRadarRainOpacity;
varying float vRainColorBlend;
varying float vRainProgress;
varying vec2 vRainDirection;
#include <fog_pars_vertex>
uniform sampler2D uMap;
uniform float uScale;
uniform float uBias;
uniform float uPointSize;
uniform float uTime;
uniform float uJitter;
uniform vec2 uMaskCenter;
uniform float uMaskRadius;
uniform float uMaskFeather;
uniform vec2 uHoverCenter;
uniform float uHoverRadius;
uniform float uHoverStrength;
uniform float uHoverSnap;
uniform float uHoverEnabled;
uniform vec3 uAirplanePos;
uniform float uAirplaneHoverEnabled;
uniform float uAirplaneHoverRadius;
// Dust wake effect uniforms
uniform float uDustWakeEnabled;
uniform float uAirplaneSpeed;
uniform float uAirplaneGroundClearance;
uniform float uDustWakeRadius;
uniform float uDustWakeHeight;
uniform float uDustWakeSpeedThreshold;
uniform float uDustWakeClearanceMax;
uniform float uDustWakeTrailLength;
uniform vec2 uAirplaneForward;
// Radar pulse
uniform float uRadarActive;
uniform vec3 uRadarOrigin;
uniform float uRadarRadius;
uniform float uRadarRingWidth;
uniform float uRadarFade;
uniform float uRadarRainHeight;
uniform float uRadarRainDuration;
uniform float uRadarTime;
uniform float uRadarExpandSpeed;
uniform float uRadarRingFeather;
uniform float uRadarPermanent;
uniform float uPersistentRevealRadius;
uniform float uRainColorFadeDuration;
uniform float uRainParticleLength;
uniform float uRainImpactScale;
uniform float uRainSpawnRadius;

// hash-based 2D noise -> scalar in [-1,1]
float hash(vec2 p){
  return -1.0 + 2.0 * fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

void main() {
  #include <uv_vertex>
  vec3 p = position;
  float h = 0.0;
  #ifdef USE_UV
  vec3 c = texture2D(uMap, vUv).rgb;
  h = (c.r + c.g + c.b) / 3.0;
  #endif
  // apply small XY jitter based on world position for seamless tiling
  if (uJitter > 0.0) {
    // Use world position for hash to ensure continuity across tile boundaries
    vec4 worldPosJitter = modelMatrix * vec4(p, 1.0);
    vec2 worldXZ = worldPosJitter.xz;
    float jx = hash(worldXZ + 0.123) * uJitter;
    float jz = hash(worldXZ + 4.567) * uJitter;
    p.x += jx;
    p.z += jz;
  }
  // base height
  float baseY = h * uScale + uBias;
  p.y = baseY;
  vec4 mvPosition = modelViewMatrix * vec4(p, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  gl_PointSize = max(1.0, uPointSize * (300.0 / max(1.0, -mvPosition.z)));
  vec4 worldPos = modelMatrix * vec4(p, 1.0);
  float d = length(worldPos.xz - uMaskCenter);
  float edge0 = uMaskRadius;
  float edge1 = uMaskRadius + max(0.0001, uMaskFeather);
  vMaskAlpha = 1.0 - smoothstep(edge0, edge1, d);
  // mouse hover influence factor 0..1 with soft falloff
  float hd = length(worldPos.xz - uHoverCenter);
  float hEdge = uHoverRadius;
  vHoverMix = uHoverEnabled * (1.0 - smoothstep(hEdge, hEdge + 0.5, hd));
  // airplane hover - distance in XZ plane from airplane
  float airDist = length(worldPos.xz - uAirplanePos.xz);
  float airEdge = uAirplaneHoverRadius;
  vAirplaneMix = uAirplaneHoverEnabled * (1.0 - smoothstep(airEdge, airEdge + 0.8, airDist));
  // proximity factor: how close is the particle to airplane vertically (0=far, 1=close)
  // Use signed distance - particles ABOVE airplane are far (green), BELOW or at same level are close (red)
  float vertDist = uAirplanePos.y - worldPos.y; // positive when airplane is above particle
  vAirplaneProximity = 1.0 - clamp(vertDist / 0.6, 0.0, 1.0); // 0=airplane high above, 1=at or below particle
  
  // Dust wake effect - particles rise and turn orange when airplane is fast and low
  vDustWakeMix = 0.0;
  float dustRise = 0.0;
  if (uDustWakeEnabled > 0.5 && uAirplaneSpeed > uDustWakeSpeedThreshold && uAirplaneGroundClearance < uDustWakeClearanceMax) {
    // Vector from airplane to particle in XZ plane
    vec2 toParticle = worldPos.xz - uAirplanePos.xz;
    float dustDistXZ = length(toParticle);
    
    // Decompose into forward/backward and side components
    // uAirplaneForward points in the direction of the trail (opposite to flight)
    vec2 trailDir = normalize(uAirplaneForward);
    float trailDist = dot(toParticle, trailDir);  // positive = in trail direction (behind plane)
    vec2 sideVec = toParticle - trailDir * trailDist;
    float sideDist = length(sideVec);
    
    // Affect particles at or below the airplane's altitude
    // More generous - includes particles close to the plane's height
    float heightDiff = uAirplanePos.y - worldPos.y;
    float belowFactor = smoothstep(-0.3, 0.3, heightDiff);  // works even if particle is slightly above
    
    // Speed intensity - faster = stronger effect (normalized from threshold to max)
    float speedFactor = smoothstep(uDustWakeSpeedThreshold, 12.0, uAirplaneSpeed);
    
    // Clearance intensity - lower = stronger effect
    float clearanceFactor = 1.0 - smoothstep(0.0, uDustWakeClearanceMax, uAirplaneGroundClearance);
    
    // === DISPLACEMENT (depression) - small area directly under airplane ===
    // Use a tighter radius for displacement to avoid obstructing the view
    float displaceRadius = uDustWakeRadius * 0.4;  // Much smaller than color radius
    float horzFactor = 1.0 - smoothstep(0.0, displaceRadius, dustDistXZ);
    float displaceIntensity = belowFactor * horzFactor * speedFactor * clearanceFactor;
    
    // Negative rise = depression (particles sink down)
    dustRise = -uDustWakeHeight * displaceIntensity;
    
    // === COLOR TRAIL - extends in the trail direction (behind the airplane) ===
    float inTrail = max(0.0, trailDist);  // how far along the trail (0 if in front of plane)
    
    // Side falloff - narrower at the back, wider near the plane
    float trailWidthAtBack = uDustWakeRadius * 0.5;
    float trailWidth = mix(uDustWakeRadius, trailWidthAtBack, smoothstep(0.0, uDustWakeTrailLength, inTrail));
    float sideFalloff = 1.0 - smoothstep(0.0, trailWidth, sideDist);
    
    // Trail color intensity - fades out along the trail length
    float trailFade = 1.0 - smoothstep(0.0, uDustWakeTrailLength, inTrail);
    
    // Near the airplane, use full intensity (circular area around plane)
    float nearFactor = 1.0 - smoothstep(0.0, uDustWakeRadius, dustDistXZ);
    
    // Combine: strong near the plane, fading cone-shaped trail behind
    float trailEffect = (inTrail > 0.0) ? sideFalloff * trailFade : 0.0;
    float colorIntensity = belowFactor * speedFactor * clearanceFactor * max(nearFactor, trailEffect);
    
    // Add some noise/variation based on position for organic look
    float dustNoise = 0.7 + 0.3 * hash(worldPos.xz * 3.14 + uTime * 0.3);
    colorIntensity *= dustNoise;
    
    // Pass to fragment shader for coloring
    vDustWakeMix = colorIntensity;
  }
  
  // Apply dust displacement to position (before radar calculations)
  // dustRise is negative for depression effect
  if (abs(dustRise) > 0.001) {
    p.y += dustRise;
    baseY += dustRise;  // Update baseY so radar animation preserves it
    mvPosition = modelViewMatrix * vec4(p, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    gl_PointSize = max(1.0, uPointSize * (300.0 / max(1.0, -mvPosition.z)));
  }
  
  // Radar pulse - expanding ring from origin with rain animation
  float radarDist = length(worldPos.xz - uRadarOrigin.xz);
  float innerEdge = uRadarRadius - uRadarRingWidth;
  float outerEdge = uRadarRadius + uRadarRingWidth;
  
  // Calculate when the radar ring reached this particle
  float ringArrivalTime = radarDist / max(0.1, uRadarExpandSpeed);
  float timeSinceArrival = uRadarTime - ringArrivalTime;
  
  // Default opacity and color blend (fully visible, final color when not in radar animation)
  vRadarRainOpacity = 1.0;
  vRainColorBlend = 1.0;
  vRainProgress = 1.0;
  vRainDirection = vec2(0.0, 1.0); // default: vertical on screen
  
  // Calculate screen-space "down" direction for rain dash orientation
  // Project world-space down vector to screen space
  vec4 worldPos4 = modelMatrix * vec4(p, 1.0);
  vec4 worldDown = worldPos4 + vec4(0.0, -1.0, 0.0, 0.0);
  vec4 clipPos = projectionMatrix * viewMatrix * worldPos4;
  vec4 clipDown = projectionMatrix * viewMatrix * worldDown;
  vec2 screenPos = clipPos.xy / clipPos.w;
  vec2 screenDown = clipDown.xy / clipDown.w;
  vec2 rainDir = normalize(screenDown - screenPos);
  // Only use this direction if it's valid (not degenerate)
  if (length(screenDown - screenPos) > 0.001) {
    vRainDirection = rainDir;
  }
  
  // Rain animation: apply to ALL particles reached by the radar pulse
  // Particles originate from near aircraft center and spread to final positions
  if (uRadarActive > 0.5 && timeSinceArrival > -0.1) {
    // Add slight randomized delay based on position for organic feel
    float randDelay = hash(vUv * 7.89) * 0.15;
    float delayedTime = timeSinceArrival - randDelay;
    float delayedProgress = clamp(delayedTime / max(0.01, uRadarRainDuration), 0.0, 1.0);
    // Easing: steady fall speed, then dramatic slowdown just before impact
    // Blend between linear (steady) and ease-out (slowing) based on progress
    float linear = delayedProgress;
    float easeOut = 1.0 - pow(1.0 - delayedProgress, 4.0);
    // Quadratic blend: more linear early, more ease-out near the end
    float blend = delayedProgress * delayedProgress;
    float delayedEased = mix(linear, easeOut, blend);
    float rainOffset = uRadarRainHeight * (1.0 - delayedEased);
    
    // Horizontal convergence: particles start near center and spread to final positions
    // Calculate direction from radar origin to particle's final position
    vec2 toParticle = worldPos.xz - uRadarOrigin.xz;
    float distFromOrigin = length(toParticle);
    vec2 dirFromOrigin = distFromOrigin > 0.01 ? toParticle / distFromOrigin : vec2(0.0);
    // At start (delayedEased=0), particles are at spawn radius from center
    // At end (delayedEased=1), particles are at their final position
    float startDist = uRainSpawnRadius;
    float horizontalOffset = mix(startDist, distFromOrigin, delayedEased) - distFromOrigin;
    vec2 horizontalShift = dirFromOrigin * horizontalOffset;
    
    // Opacity ramps from 0 to 1 as particles fall into place
    // Distance-based start opacity: 0.5 near center (visible), 1.0 far out
    float distOpacityBase = mix(0.5, 1.0, smoothstep(2.0, 15.0, distFromOrigin));
    // Fade from base opacity to 1.0 as it lands
    vRadarRainOpacity = mix(distOpacityBase, 1.0, delayedEased);
    
    // Pass LINEAR progress (not eased) for shape transition - this keeps dashes visible longer
    vRainProgress = delayedProgress;
    
    // Color crossfade: starts after landing, transitions over uRainColorFadeDuration
    // Time since particle landed (delayedEased reached 1.0)
    float landingTime = randDelay + uRadarRainDuration;
    float timeSinceLanding = max(0.0, delayedTime - uRadarRainDuration);
    float colorFadeProgress = clamp(timeSinceLanding / max(0.01, uRainColorFadeDuration), 0.0, 1.0);
    // 0 = rain color, 1 = ring/final color
    vRainColorBlend = colorFadeProgress;
    
    // Update world position for the rain effect (vertical + horizontal)
    worldPos.y += rainOffset;
    worldPos.x += horizontalShift.x;
    worldPos.z += horizontalShift.y;
    // Also update the gl_Position and point size
    vec3 animatedP = p;
    animatedP.y = baseY + rainOffset;
    animatedP.x += horizontalShift.x;
    animatedP.z += horizontalShift.y;
    mvPosition = modelViewMatrix * vec4(animatedP, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    // Two-phase size transition:
    // Phase 1 (0.7-0.9): shrink from rainScale to impactScale (visible impact)
    // Phase 2 (0.9-1.0): shrink from impactScale to 1.0 (settle to final size)
    float rainScale = uRainParticleLength * 4.0;
    float phase1 = smoothstep(0.7, 0.9, delayedProgress);
    float phase2 = smoothstep(0.9, 1.0, delayedProgress);
    float fallScale = mix(rainScale, uRainImpactScale, phase1);
    fallScale = mix(fallScale, 1.0, phase2);
    gl_PointSize = max(1.0, uPointSize * fallScale * (300.0 / max(1.0, -mvPosition.z)));
  }
  
  // Ring effect: particles within the ring band (with feathering)
  float feather = max(0.1, uRadarRingFeather);
  float ringMix = smoothstep(innerEdge - feather, innerEdge, radarDist) 
                * (1.0 - smoothstep(outerEdge, outerEdge + feather, radarDist));
  // Persistent reveal uses the max of current radius and persistent radius
  // This ensures particles stay lit even when a new pulse starts
  float persistentEdge = max(outerEdge, uPersistentRevealRadius);
  float revealMix = 1.0 - smoothstep(persistentEdge - feather * 0.5, persistentEdge + feather, radarDist);
  
  // vRadarMix controls the visibility/reveal strength
  // If permanent mode is on, we force it to a strong reveal level (0.8)
  vRadarMix = max(uRadarActive * max(ringMix * 1.5, revealMix * 0.8), uRadarPermanent * 0.8);
  // Height for coloring (normalized 0-1 based on final world Y, not animated)
  vRadarHeight = clamp((baseY + 1.0) / 3.0, 0.0, 1.0);
  #include <fog_vertex>
}
